import { useTranslation } from 'react-i18next';
import { Avatar, AvatarFallback } from '@/shared/ui/avatar';
import {
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/shared/ui/dropdown-menu';
import { usePrincipal } from '@/core/auth/appToken';
import type { SelectableOrganization } from '@/models/principal';
import { useSwitchActiveLab } from './useSwitchActiveLab';

// Full logos (symbol + wordmark) are unreadable at icon size; initials until a cropped icon exists.
function LabInitials({ lab }: { lab: SelectableOrganization }) {
  return (
    <Avatar className="h-6 w-6 rounded-md">
      <AvatarFallback className="rounded-md text-xs" aria-hidden="true">
        {lab.name.slice(0, 2).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

/** Lab switch submenu of the user menu; must be placed inside a `DropdownMenuContent`. */
export function ActiveLabSubmenu() {
  const labs = usePrincipal()?.selectableOrganizations ?? [];
  const active = labs.find((lab) => lab.active);
  if (labs.length < 2 || active === undefined) return null;
  return <LabSwitchEntries labs={labs} active={active} />;
}

/** Separate component so the data-router hooks of the switch only run when the entry is shown. */
function LabSwitchEntries({
  labs,
  active,
}: {
  labs: readonly SelectableOrganization[];
  active: SelectableOrganization;
}) {
  const { t } = useTranslation('app');
  const { mutate: switchLab, isPending } = useSwitchActiveLab();

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger
          className="gap-2"
          aria-label={t('portal.header.activeLab.trigger', { name: active.name })}
        >
          <LabInitials lab={active} />
          <span className="flex min-w-0 max-w-60 flex-col items-start text-left">
            <span className="text-xs text-muted-foreground">{t('portal.header.activeLab.current')}</span>
            <span className="w-full truncate text-sm font-medium" title={active.name}>
              {active.name}
            </span>
          </span>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          <DropdownMenuRadioGroup
            aria-label={t('portal.header.activeLab.menuLabel')}
            value={active.organizationId}
            onValueChange={(id) => {
              if (!isPending && id !== active.organizationId) switchLab(id);
            }}
          >
            {labs.map((lab) => (
              <DropdownMenuRadioItem
                key={lab.organizationId}
                value={lab.organizationId}
                disabled={isPending}
                className="gap-2"
              >
                <LabInitials lab={lab} />
                <span className="max-w-60 truncate" title={lab.name}>
                  {lab.name}
                </span>
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSeparator />
    </>
  );
}
